<script setup lang="ts">
import { computed } from 'vue';

import { useStatsigEventsStore } from '@apps/main/new-desktop/core/stores';
import type { HotelFilter } from '@apps/main/new-desktop/modules/hotelFilters';

import { AppPreferencesTags } from '@libs/ui';

import type {
  ItineraryAccommodationLoaderSegment,
  ItineraryAccommodationSegment,
} from '../../../models';
import {
  isUnavailableAccommodationSegment,
  isAlternativeRoomSuggestionAccommodationSegment,
  UnavailabilityReason,
  isItineraryAccommodationLoaderSegment,
} from '../../../models';

const statsigEventsStore = useStatsigEventsStore();

interface AccommodationSegmentHeaderProps {
  segment: ItineraryAccommodationSegment | ItineraryAccommodationLoaderSegment;
  printMode: boolean;
}

const props = defineProps<AccommodationSegmentHeaderProps>();

interface AvailableAccommodationSegmentEmits {
  (eventName: 'add-filter'): void;
  (eventName: 'remove-filter', id: HotelFilter): void;
  (eventName: 'show-more-filters'): void;
  (eventName: 'toggle-accommodation'): void;
}

const emit = defineEmits<AvailableAccommodationSegmentEmits>();

const shouldShowPreferencesTags = computed<boolean>(() => {
  if (isItineraryAccommodationLoaderSegment(props.segment)) {
    return true;
  }
  if (isAlternativeRoomSuggestionAccommodationSegment(props.segment)) {
    return false;
  }
  if (isUnavailableAccommodationSegment(props.segment)) {
    return props.segment.reasons.includes(UnavailabilityReason.SelectedFilters);
  }
  return true;
});
</script>

<template>
  <div :class="['accommodation-segment-header']">
    <template v-if="!props.printMode">
      <AppPreferencesTags
        v-if="shouldShowPreferencesTags"
        :tags="props.segment.filters"
        :add-preferences-text="$t('filter.add_filters')"
        :should-hide-add-button="true"
        class="preferences-tags"
        @add-filter="
          statsigEventsStore.logTripOverviewFilterButtonClicked();
          emit('add-filter');
        "
        @show-more-filters="emit('show-more-filters')"
        @remove-filter="emit('remove-filter', $event as HotelFilter)"
      />
    </template>
  </div>
</template>

<style scoped lang="scss">
.accommodation-segment-header {
  width: 100%;

  display: grid;
  grid-template-columns: min-content min-content;
  grid-template-rows: auto;
  align-items: center;
  margin-left: var(--spacing-12);

  .preferences-tags {
    grid-column: 3/4;
    grid-row: 1/-1;
    margin-right: var(--spacing-16);
  }

  @include for-less-or-equal-than-599-px {
    display: block;

    grid-template-columns: auto auto;
    grid-template-rows: auto auto;
  }
}
</style>
